import React, { FocusEventHandler, forwardRef } from 'react'
import MaskedInput from 'react-text-mask'
import { InputBaseComponentProps } from '@mui/material/InputBase/InputBase'
import * as R from 'ramda'

import { CountryCode } from '../../types'
import { handleMaskInputFocus } from '../../utils'

/*
  We support:
  US ZIP format: XXXXX
  US ZIP+4 format: XXXXX-XXXX
  CA ZIP format: AXA XAX
  ES ZIP format: XXXXX
  where X=digits 0–9, A=letters A-Z
*/

const CANADIAN_MASK = [
  /(?!.*[dfioquDFIOQU])[a-vxyA-VXY]/,
  /[0-9]/,
  /(?!.*[dfioquDFIOQU])[a-zA-Z]/,
  ' ',
  /[0-9]/,
  /(?!.*[dfioquDFIOQU])[a-zA-Z]/,
  /[0-9]/,
]

const SPAIN_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/]

const USA_MASK_SHORT = [/\d/, /\d/, /\d/, /\d/, /\d/]
// @ts-ignore
const USA_MASK_LONG = USA_MASK_SHORT.concat(['-', /\d/, /\d/, /\d/, /\d/])

const FREE_MASK = Array.from(Array(10)).map(() => /[A-Za-z0-9-/\s]/)

const getUSAMask = (rawValue: string) =>
  rawValue.length > 5 ? USA_MASK_LONG : USA_MASK_SHORT

const getMask = (country?: CountryCode) => {
  switch (country) {
    case CountryCode.CA:
      return CANADIAN_MASK
    case CountryCode.ES:
      return SPAIN_MASK
    case CountryCode.US:
      return getUSAMask
    default:
      return FREE_MASK
  }
}

export interface ZipInputProps
  extends Omit<Partial<InputBaseComponentProps>, 'onFocus'> {
  readonly country?: CountryCode
  readonly onFocus?: FocusEventHandler<HTMLInputElement>
}

const ZipInput = forwardRef<HTMLInputElement, ZipInputProps>(function ZipInput(
  { onChange, onFocus, country, mask, ...rest },
  ref,
) {
  return (
    <MaskedInput
      guide={false}
      mask={mask || getMask(country)}
      placeholderChar={'\u2000'}
      ref={inputRef => {
        if (R.is(Function, ref)) {
          const inputElement = inputRef?.inputElement as HTMLInputElement
          ref(inputElement)
        }
      }}
      onChange={event => {
        const { target: { value = '' } = {} } = event
        const newEvent = {
          ...event,
          target: {
            ...event.target,
            value: value.toLocaleUpperCase(),
          },
        }
        if (onChange) {
          onChange(newEvent)
        }
      }}
      onFocus={handleMaskInputFocus(onFocus)}
      {...rest}
    />
  )
})

export default ZipInput
